'use client'

import { css } from '@lastbrand/web-ui-styled-system/css'
import { useTranslation } from 'next-i18next'
import { Dispatch, SetStateAction, useCallback, useEffect } from 'react'

import { CollectionFiltersDS } from '@src/components/blocks/collection-filters-ds/collection-filters-ds'
import { LoaderWrapperDS } from '@src/components/blocks/loader-wrapper-ds'
import { SearchResultsDS } from '@src/components/blocks/search-results-ds'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { SectionContentContainer } from '@src/ds/blocks/section-content-container'
import { SelectedFilter } from '@src/pages/[...slug]/components/collection/domains/CollectionFilter'
import { useSearchResults } from '@src/pages/search/searchResultsSection/hooks/useSearchResults'
import { UpdateQueryParams } from '@src/quince-components/src/atoms/filterSection/menu'
import useRecurringItemSubscriptionsExperiment from '@src/quince-site-common/src/hooks/useRecurringItemSubscriptionsExperiment'
import SomethingWentWrong from '@src/quince-site-common/src/organisms/somethingWentWrong/somethingWentWrong'

/* eslint-disable i18next/no-literal-string -- Panda design tokens and raw lengths, not user-facing copy */

const headerBandClass = css({
  py: { base: 'space-20', lg: 'space-24' },
})

const headerContentClass = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: { base: 'center', lg: 'flex-start' },
  gap: 'space-8',
  wordBreak: 'break-word',
})

const titleClass = css({
  w: 'full',
  textStyle: { base: 'title-14', lg: 'title-20' },
  textTransform: 'uppercase',
  textAlign: { base: 'center', lg: 'left' },
  color: 'black',
})

const countClass = css({
  w: 'full',
  textStyle: 'body-12',
  textAlign: { base: 'center', lg: 'left' },
  color: { base: 'mediumgray', lg: 'gray' },
})

// Mobile mirrors the bar's own 8px bottom spacing, so the chip row stays evenly padded when the
// search header above it is absent. Belongs on a wrapper rather than on the bar itself: the bar
// goes `position: fixed` once it sticks, and padding on it would stop holding the chips down.
export const filterBarTopInsetClass = css({
  pt: { base: 'space-8', lg: 'space-32' },
})

/* eslint-enable i18next/no-literal-string */

// The error state gets no wrapper class on purpose: legacy's `.somethingWentWrongWrapper` was never
// resolvable from the stylesheet it was imported through, so the message has always rendered flush
// to the top. Giving it a gap would be a new design decision, not a parity fix.

// eslint-disable-next-line i18next/no-literal-string -- storage scope key, not user-facing copy
const SEARCH_FILTER_STATE_SCOPE = '/search'

interface SearchResultsSectionDSProps {
  setSearchLoaded: Dispatch<SetStateAction<boolean>>
}

export function SearchResultsSectionDS({ setSearchLoaded }: SearchResultsSectionDSProps) {
  const isRecurringItemSubscriptionsExpEnabled = useRecurringItemSubscriptionsExperiment()

  const {
    productCards,
    isLayoutLoading,
    isRefetching,
    filtersV2Enabled,
    isError,
    searchQuery,
    intersectRef,
    isMoreProductsLoading,
    totalProducts,
    filters,
    selectedFilters,
    updateQuery,
    hideSearchHeader,
  } = useSearchResults(isRecurringItemSubscriptionsExpEnabled)

  useEffect(() => {
    setSearchLoaded(!isLayoutLoading)
  }, [isLayoutLoading, setSearchLoaded])

  const { t } = useTranslation('common')

  const hasAppliedFilters = SelectedFilter.isAnyFilterSelected(selectedFilters)
  const shouldRenderFilters = totalProducts > 0 || hasAppliedFilters

  const handleResetFilters = useCallback(() => {
    updateQuery({ filter: { type: 'reset' } })
  }, [updateQuery])

  if (isError) {
    return (
      <SomethingWentWrong
        // px, not rem: legacy's 20rem resolved against a 10px root, the DS root is 16px.
        // eslint-disable-next-line i18next/no-literal-string -- CSS length value, not user-facing copy
        minHeight={'200px'}
      />
    )
  }

  const renderFilterSection = filters.length > 0 && shouldRenderFilters

  return (
    <div>
      {/* eslint-disable-next-line i18next/no-literal-string -- CSS length value */}
      <LoaderWrapperDS showLoader={isLayoutLoading} minHeight={'16rem'} loaderSize={40}>
        {searchQuery && !hideSearchHeader && totalProducts > 0 ? (
          <SectionContentContainer className={headerBandClass} contentClassName={headerContentClass}>
            <h1 className={titleClass} data-testid={TEST_LOCATORS.SEARCH_RESULTS_TITLE}>{`"${searchQuery}"`}</h1>
            <p className={countClass}>
              {`${t('showing')} ${t(totalProducts === 1 ? 'totalitems_item' : 'totalitems_items', {
                totalItems: totalProducts,
              })}`}
            </p>
          </SectionContentContainer>
        ) : null}

        {/* eslint-disable-next-line i18next/no-literal-string -- DOM id used by search-page tests / observers */}
        <div id={'newSearchSection'}>
          {renderFilterSection ? (
            // Only the class is conditional; making the wrapper itself conditional would remount the
            // bar every time `totalProducts` crossed zero and drop its open sheet mid-interaction.
            <div className={hideSearchHeader || !totalProducts ? filterBarTopInsetClass : undefined}>
              <CollectionFiltersDS
                noOfItems={totalProducts}
                filters={filters}
                selectedFilters={selectedFilters as unknown as Record<string, string[]>}
                updateQuery={updateQuery as (params: UpdateQueryParams) => void}
                skipSorting
                // In control (filtersV2Enabled false) a filter-triggered refetch flips isLayoutLoading,
                // hiding the LoaderWrapper children and unmounting the bar via showShimmer — keeping
                // the Radix popover portal from orphaning an open dropdown at (0,0). In treatment
                // isLayoutLoading stays false during a refetch so the bar (and open dropdown) stay mounted.
                showShimmer={isLayoutLoading}
                collectionSlug={SEARCH_FILTER_STATE_SCOPE}
              />
            </div>
          ) : null}

          <SearchResultsDS
            searchQuery={searchQuery}
            productCards={productCards}
            intersectRef={intersectRef}
            isMoreProductsLoading={isMoreProductsLoading}
            onResetFilters={handleResetFilters}
            selectedFilters={selectedFilters}
            isRefetching={isRefetching}
            filtersV2Enabled={filtersV2Enabled}
          />
        </div>
      </LoaderWrapperDS>
    </div>
  )
}
